import React, { useState } from 'react';
import { Image, Upload, X } from 'lucide-react';

const ImageUploadModal = ({ zaakId, onClose, onSuccess }) => {
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(null);
    const [file, setFile] = useState(null);
    const [description, setDescription] = useState('');
    const [orderNumber, setOrderNumber] = useState(null);

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreview(reader.result);
            };
            reader.readAsDataURL(selectedFile);
        }
    };

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!file) return;
        
        setUploading(true);
        const formData = new FormData();
        formData.append('image', file);
        formData.append('description', description);
        formData.append('order_number', orderNumber);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/analyses/${zaakId}/images`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) throw new Error('Upload failed');

            const imageData = await response.json();
            onSuccess(imageData);
            onClose();
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-lg w-full max-w-3xl my-4">
                <div className="flex justify-between items-center p-6 border-b">
                    <h2 className="text-xl font-semibold">Upload Afbeelding</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleUpload} className="p-6">
                    <div className="max-h-[calc(100vh-20rem)] overflow-y-auto">
                        {!preview ? (
                            <div className="border-2 border-dashed rounded-lg p-8 text-center mb-4">
                                <label className="cursor-pointer">
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleFileSelect}
                                    />
                                    <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                    <p className="text-sm text-gray-500">Klik om een afbeelding te uploaden</p>
                                </label>
                            </div>
                        ) : (
                            <div className="relative mb-4">
                                <div className="max-h-96 overflow-hidden rounded-lg">
                                    <img 
                                        src={preview} 
                                        alt="Preview" 
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setFile(null);
                                        setPreview(null);
                                    }}
                                    className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-sm hover:bg-gray-100"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        )}

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Beschrijving
                                </label>
                                <textarea
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="w-full border rounded-lg p-2 h-20"
                                    placeholder="Voeg een beschrijving toe..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Volgorde
                                </label>
                                <input
                                    type="number"
                                    value={orderNumber || ''}
                                    onChange={(e) => setOrderNumber(e.target.value ? parseInt(e.target.value) : null)}
                                    className="w-full border rounded-lg p-2"
                                    min="0"
                                    placeholder="Optioneel"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end gap-3 border-t pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                        >
                            Annuleren
                        </button>
                        <button
                            type="submit"
                            disabled={!file || uploading}
                            className="px-4 py-2 bg-nn-500 text-white rounded-lg hover:bg-nn-600 disabled:opacity-50"
                        >
                            {uploading ? 'Uploaden...' : 'Upload afbeelding'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ImageUploadModal;